/**
 * @fileoverview Modelos de request/response de CR Auth y de la definición de contraseña en portal-api.
 * @module integrations/models/auth.models
 */

/** Códigos de error de CR Auth (campo `code` del cuerpo de error). */
export const AUTH_ERROR_CODES = Object.freeze({
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTH_INVALID_CREDENTIALS: 'AUTH_INVALID_CREDENTIALS',
  AUTH_ACCOUNT_LOCKED: 'AUTH_ACCOUNT_LOCKED',
  AUTH_PASSWORD_EXPIRED: 'AUTH_PASSWORD_EXPIRED',
  AUTH_PASSWORD_NOT_SET: 'AUTH_PASSWORD_NOT_SET',
  AUTH_CAPTCHA_INVALID: 'AUTH_CAPTCHA_INVALID',
  AUTH_CAPTCHA_PROVIDER_ERROR: 'AUTH_CAPTCHA_PROVIDER_ERROR',
  AUTH_USER_ALREADY_REGISTERED: 'AUTH_USER_ALREADY_REGISTERED',
  AUTH_REFRESH_TOKEN_INVALID: 'AUTH_REFRESH_TOKEN_INVALID'
})

/** Código cuando la llamada no obtiene respuesta legible (red, timeout o 401 de Apigee sin CORS). */
export const NETWORK_ERROR_CODE = 'NETWORK_ERROR'

/**
 * @typedef {Object} LoginRequest
 * @property {('DNI'|'CE')} documentType
 * @property {string} documentNumber
 * @property {string} password
 * @property {string} recaptchaToken - Acción `login`
 * @property {string} [origin]
 */

/**
 * @typedef {Object} SessionTokens
 * @property {string} accessToken - JWT RS256 (15 min)
 * @property {string} refreshToken - Opaco y rotativo (8 h)
 * @property {string} tokenType - Siempre `Bearer`
 * @property {number} expiresIn - Segundos de vida del access token
 * @property {number} refreshExpiresIn - Segundos de vida del refresh token
 */

/**
 * @typedef {Object} LoginResponse
 * @property {string} accessToken
 * @property {string} refreshToken
 * @property {string} tokenType
 * @property {number} expiresIn
 * @property {number} refreshExpiresIn
 * @property {boolean} mustChangePassword - La clave guardada es un código temporal del sistema anterior
 * @property {{expiresInDays: (number|undefined), aboutToExpire: boolean}} password
 * @property {{id: string, firstName: (string|undefined), lastName: (string|undefined), secondLastName: (string|undefined), age: (number|undefined)}} user
 * @property {Object.<string, {show: boolean, message: (string|undefined)}>} features
 */

/**
 * @typedef {Object} OtpRequest
 * @property {('DNI'|'CE')} documentType
 * @property {string} documentNumber
 * @property {('EMAIL'|'SMS')} channel
 * @property {string} recaptchaToken - Acción `signup` o `password_recovery`
 * @property {string} [origin]
 */

/**
 * @typedef {Object} OtpChallenge
 * @property {string} verificationId
 * @property {('EMAIL'|'SMS')} channel
 * @property {string} [maskedDestination]
 * @property {number} expiresInSeconds
 * @property {number} resendAvailableInSeconds
 * @property {number} remainingResends
 */

/**
 * @typedef {Object} OtpResendRequest
 * @property {string} verificationId
 * @property {string} recaptchaToken - Acción `otp_resend`
 * @property {string} [origin]
 */

/**
 * @typedef {Object} OtpVerifyRequest
 * @property {string} verificationId
 * @property {string} code - 6 dígitos
 */

/**
 * @typedef {Object} OtpVerificationResult
 * @property {string} passwordSetupToken - JWT de un solo uso (aud `zonasegura-web:password-setup`)
 * @property {number} expiresIn - Segundos de vida del token
 */

/**
 * @typedef {Object} PasswordSetupResponse
 * @property {string} codigoRespuesta - 01 éxito, 98 formato inválido, 97 igual a la anterior, 99 error
 * @property {string} mensajeRespuesta
 */

/**
 * Error controlado de una integración. Nunca expone el cuerpo crudo ni el stacktrace.
 * @typedef {Error} IntegrationError
 * @property {number} status - Status HTTP; 0 si no hubo respuesta legible
 * @property {string} code - `code` de CR Auth, `HTTP_<status>` o `NETWORK_ERROR`
 * @property {(string|null)} transactionId - `transactionId` de CR Auth, si vino
 * @property {(Object|null)} details - `details` de CR Auth (p. ej. `remainingAttempts`)
 * @property {(Object|null)} data - Cuerpo de la respuesta (portal-api responde con `codigoRespuesta`)
 */



// WEBPACK FOOTER //
// ./src/integrations/models/auth.models.js